import {
  AnnotationOnImage,
  AnnotationDefinitionCore,
  AnnotationVersion,
  AnnotationDefinition,
  convertAnnotationVersionToAnnotationDefinitionCore,
  convertAnnotationDefinitionToAnnotationDefinitionCore,
  CursorDimensions,
  AnnotationType,
  AnnotationsLayers,
} from '../../models/annotation.types';
import { ExamScoringSchema, ExamTask, ExamTaskGroupSolutionScoreShelfResponse } from '../../models/assessment.types';
import { isChrome, isFirefox, isSafari } from '../../utils/detectBrowser';
import { svgToPng, SVGToPNGResult } from '../../utils/svg';
import {
  ANNOTATION_DEFAULT_SCALE,
  ANNOTATION_ICON_SIZE,
  ANNOTATION_OBJECT_NAME,
  DEFAULT_ANNOTATION_HEIGHT,
  DEFAULT_ANNOTATION_WIDTH,
  DIFFERENT_CRITERION_OPACITY,
} from './annotationsConsts';
import { AnnotationImageData, Coords, pageImageData, rotateAngleValue } from './Preview.types';
import { convertToViolet, getImageRotateOffsetForIcons, getPointRelativeToImage, getTargetPageImage } from './previewUtils';
import { fabric } from 'fabric';

export const getCurrentAnnotationScale = (): number => {
  return ANNOTATION_ICON_SIZE / 20;
};

export const getAnnotationDefinitionCore = (
  annotation: AnnotationOnImage,
  activeTask: ExamTask | null,
  annotationVersions: AnnotationVersion[],
  annotationDefinitions: AnnotationDefinition[],
): AnnotationDefinitionCore | null => {
  const annotationBelongsToActiveTask = activeTask?.examScoringSchemas.find(
    (item: ExamScoringSchema) => item.examScoringSchemaId === annotation.examScoringSchemaId,
  );

  if (annotationBelongsToActiveTask) {
    let annotationDefinitionCore: AnnotationDefinitionCore | null = null;
    const annotationVersion = annotationVersions.find(
      (item: AnnotationVersion) => item.id === annotation.annotationDefinitionVersionId,
    );
    if (annotationVersion) {
      annotationDefinitionCore = convertAnnotationVersionToAnnotationDefinitionCore(annotationVersion);
    } else {
      const annotationDefinition = annotationDefinitions.find(
        (item: AnnotationDefinition) => item.id === annotation.annotationDefinitionId,
      );
      if (annotationDefinition) {
        annotationDefinitionCore = convertAnnotationDefinitionToAnnotationDefinitionCore(annotationDefinition);
      }
    }

    return annotationDefinitionCore;
  }
  return null;
};

const getAnnotationWidth = (
  cursorDimensions: CursorDimensions | null,
  defaultAnnotationSizeRef?: React.MutableRefObject<CursorDimensions | null> | null,
) => {
  if (cursorDimensions) return cursorDimensions.width;
  if (defaultAnnotationSizeRef?.current) return defaultAnnotationSizeRef.current.width;
  return DEFAULT_ANNOTATION_WIDTH;
};

const geAnnotationHeight = (
  cursorDimensions: CursorDimensions | null,
  defaultAnnotationSizeRef?: React.MutableRefObject<CursorDimensions | null> | null,
) => {
  if (cursorDimensions) return cursorDimensions.height;
  if (defaultAnnotationSizeRef?.current) return defaultAnnotationSizeRef.current.height;
  return DEFAULT_ANNOTATION_HEIGHT;
};

export const setupDynamicAnnotation = (
  annotation: AnnotationOnImage,
  annotationDefinitionCore: AnnotationDefinitionCore,
  cursorDimensions: CursorDimensions | null,
  image?: fabric.Object,
  defaultAnnotationSizeRef?: React.MutableRefObject<CursorDimensions | null> | null,
): void => {
  const defaultScale = getCurrentAnnotationScale();
  const defaultWidth = getAnnotationWidth(cursorDimensions, defaultAnnotationSizeRef);
  const defaultHeight = geAnnotationHeight(cursorDimensions, defaultAnnotationSizeRef);

  annotation.angle = image && image.angle ? image.angle : 0;
  annotation.width = image && image.width && image.scaleX ? (image.width * image.scaleX) / defaultScale : defaultWidth;
  if (!annotationDefinitionCore.isRepetitive) {
    annotation.height =
      image && image.height && image.scaleY ? (image.height * image.scaleY) / defaultScale : defaultHeight;
  }
};

export const getOpacityForAnnotation = (
  annotation: AnnotationOnImage,
  activeCriterion: ExamScoringSchema | null,
): number => {
  return annotation.examScoringSchemaId === activeCriterion?.examScoringSchemaId ? 1 : DIFFERENT_CRITERION_OPACITY;
};

export const setImageCoords = (
  annotation: AnnotationOnImage,
  image: fabric.Object,
  annotationDefinitionCore: AnnotationDefinitionCore,
  rotateAngle: rotateAngleValue,
  currentCoords: Coords | null | undefined,
): void => {
  if (annotation.positionX !== null && annotation.positionY !== null && currentCoords) {
    const { leftOffset, topOffset } = getImageRotateOffsetForIcons(
      rotateAngle,
      annotation.positionX,
      annotation.positionY,
    );
    image.set({
      left: currentCoords.left + leftOffset,
      top: currentCoords.top + topOffset,
    });

    if (annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
      image.angle = annotation.angle !== null ? annotation.angle - rotateAngle : rotateAngle;
    }
  }
};

export const createNewAnnotationPositionBasedOnImage = (
  pageImages: pageImageData[],
  image: fabric.Object,
  annotation: AnnotationOnImage,
): AnnotationOnImage | null => {
  if (!image.left || !image.top) return null;

  const targetImageData: pageImageData | undefined = getTargetPageImage(pageImages, image.left, image.top);
  if (!targetImageData) return null;

  const coords = getPointRelativeToImage(image.left, image.top, targetImageData);
  const updatedAnnotation: AnnotationOnImage = { ...annotation } as AnnotationOnImage;
  updatedAnnotation.positionX = coords.x;
  updatedAnnotation.positionY = coords.y;
  updatedAnnotation.pageNumber = targetImageData.pageNumber;
  return updatedAnnotation;
};

export const customNumberRound = (num: number | null) => {
  if (num === null) return null;
  const decimalPart = num - Math.floor(num);
  if (decimalPart < 0.00005) {
    return Math.floor(num);
  }
  return num;
};

export const isAnnotationPositionChanged = (
  updatedAnnotation: AnnotationOnImage,
  annotation: AnnotationOnImage,
): boolean => {
  return (
    updatedAnnotation.positionX !== annotation.positionX ||
    updatedAnnotation.positionY !== annotation.positionY ||
    updatedAnnotation.pageNumber !== annotation.pageNumber ||
    customNumberRound(updatedAnnotation.width) !== customNumberRound(annotation.width) ||
    customNumberRound(updatedAnnotation.height) !== customNumberRound(annotation.height) ||
    updatedAnnotation.angle !== annotation.angle
  );
};

export const setDynamicAnnotationScale = (
  annotationDefinitionCore: AnnotationDefinitionCore,
  annotation: AnnotationOnImage,
  image: fabric.Object,
  defaultScale: number,
  rotateAngle: rotateAngleValue,
  withEventsCallbacks: boolean,
): void => {
  if (annotationDefinitionCore.isRepetitive) {
    image.setControlsVisibility({
      bl: false,
      br: false,
      mb: false,
      mt: false,
      tl: false,
      tr: false,
    });
    if (withEventsCallbacks) {
      image.on('scaling', () => {
        if (image.width !== undefined && image.scaleX !== undefined) {
          const w = (image.width * image.scaleX) / defaultScale;
          image.set({
            width: w,
            scaleX: defaultScale,
          });
        }
      });
    }
  } else {
    image.set({
      scaleX:
        annotation.width !== null && image.width !== undefined && image.width !== 0
          ? (annotation.width / image.width) * ANNOTATION_DEFAULT_SCALE
          : ANNOTATION_DEFAULT_SCALE,
      scaleY:
        annotation.height !== null && image.height !== undefined && image.height !== 0
          ? (annotation.height / image.height) * ANNOTATION_DEFAULT_SCALE
          : ANNOTATION_DEFAULT_SCALE,
    });
  }
  image.set({
    angle: (annotation.angle ?? 0) + rotateAngle,
    noScaleCache: false,
    strokeUniform: true,
  });
};

export const getAnnotationIdFromImg = (img: fabric.Object): string | null => {
  if (!img.name) return null;
  const id = img.name.substring(ANNOTATION_OBJECT_NAME.length);
  if (id === '') return null;
  return id;
};

export const beforeRemove = (
  annotation: AnnotationOnImage,
  responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
  activeCriterion: ExamScoringSchema | null,
): AnnotationOnImage | null => {
  const annotationToRemove = !responseData
    ? annotation
    : responseData.annotationsDto.dtos.find((item) => {
      const compareItem = annotation;
      const itemAuthorId = item.createdByExaminerId ?? item.examinerId;
      const compareItemAuthorId = compareItem.createdByExaminerId ?? compareItem.examinerId;
      return (
        item.annotationDefinitionId === compareItem.annotationDefinitionId &&
        itemAuthorId === compareItemAuthorId &&
        item.pageNumber === compareItem.pageNumber &&
        item.positionX === compareItem.positionX &&
        item.positionY === compareItem.positionY
      );
    });

  if (activeCriterion && annotationToRemove) {
    return annotationToRemove;
  }
  return null;
};

export const calculateHorizontalScrollForMovingAnnotation = (
  imageLeft: number,
  scrollLeft: number,
  padding: number,
  scrollStep: number,
  imgWidth: number,
  canvasWidth: number,
): number | null => {
  // Left
  if (imageLeft < scrollLeft + padding) {
    return scrollLeft - scrollStep;
  }

  // Right
  else if (imageLeft + imgWidth + padding > scrollLeft + canvasWidth - padding) {
    return scrollLeft + scrollStep;
  }

  return null;
};

export const calculateVerticalScrollForMovingAnnotation = (
  imageTop: number,
  scrollTop: number,
  padding: number,
  scrollStep: number,
  imgHeight: number,
  canvasHeight: number,
): number | null => {
  // Up
  if (imageTop < scrollTop + padding) {
    return scrollTop - scrollStep;
  }

  // Down
  else if (imageTop + imgHeight + padding > scrollTop + canvasHeight - padding) {
    return scrollTop + scrollStep;
  }

  return null;
};

export const isAnnotationActive = (
  annotation: AnnotationOnImage,
  activeCriterion: ExamScoringSchema | null,
): boolean => {
  return activeCriterion !== null && annotation.examScoringSchemaId === activeCriterion.examScoringSchemaId;
};

const getAnnotationSvgColorKey = (svg: string): string => {
  const svgDocument = new DOMParser().parseFromString(svg, 'image/svg+xml');
  const colors = new Set<string>();

  svgDocument.querySelectorAll('*').forEach((element) => {
    const svgElement = element as SVGElement;
    ['fill', 'stroke', 'color'].forEach((property) => {
      const attributeValue = element.getAttribute(property)?.trim();
      const styleValue = svgElement.style?.getPropertyValue(property).trim();
      if (attributeValue) colors.add(`${property}:${attributeValue}`);
      if (styleValue) colors.add(`${property}:${styleValue}`);
    });
  });

  return Array.from(colors).sort().join(',');
};

export const getAnnotationIconCacheKey = (
  annotationDefinitionCore: AnnotationDefinitionCore,
  annotationDefinitionVersionId?: string,
): string => {
  const iconVersion = annotationDefinitionVersionId ?? getAnnotationSvgColorKey(annotationDefinitionCore.icon);
  return `${annotationDefinitionCore.annotationDefinitionId}-${iconVersion}`;
};

export const createPNGCacheKey = (isGrey: boolean, isViolet: boolean, annotationIconCacheKey: string): string => {
  return `${+isGrey}-${+isViolet}-${annotationIconCacheKey}`;
};

export const createAnnotationImage = async (
  annotation: AnnotationOnImage,
  annotationDefinitionCore: AnnotationDefinitionCore,
  addingAnnotationEnabled: boolean,
  activeTask: ExamTask | null,
  pngCache: Map<string, SVGToPNGResult>,
  svgCache: Map<string, fabric.Object>,
  isVerifierAnnotation = false,
): Promise<fabric.Object | null> => {
  const annotationName = ANNOTATION_OBJECT_NAME + annotation.annotationId;
  const disableCache = isFirefox() || isSafari();

  if (annotationDefinitionCore.annotationType === AnnotationType.dynamic && annotationDefinitionCore.isRepetitive) {
    const isGrey = Boolean(!addingAnnotationEnabled || (activeTask && activeTask.noAnswer)) && !isVerifierAnnotation;
    const isViolet = isVerifierAnnotation;
    const annotationIconCacheKey = getAnnotationIconCacheKey(
      annotationDefinitionCore,
      annotation.annotationDefinitionVersionId,
    );
    const key = createPNGCacheKey(isGrey, isViolet, annotationIconCacheKey);

    let pngData = disableCache ? undefined : pngCache.get(key);
    if (!pngData) {
      pngData = await svgToPng(annotationDefinitionCore.icon, 0, isGrey, isViolet);
      if (!pngData) return null;
      if (!disableCache) pngCache.set(key, pngData);
    }

    if (pngData !== undefined) {
      const pngDataCopy = pngData;
      const img = fabric.util.createImage();

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => {
          console.error('Failed to load annotation image:', annotationDefinitionCore.annotationDefinitionId);
          reject(new Error('Image load failed'));
        };

        img.src = pngData!.dataUrl;
      }).catch((e) => {
        console.error('Problem while resolving annotation img', { e, annotation });
      });

      const pattern = new fabric.Pattern({
        source: img,
        repeat: 'repeat',
      });
      const image = new fabric.Rect({ fill: pattern });
      image.set({ width: annotation.width ?? pngDataCopy.width, height: pngDataCopy.height, name: annotationName });

      return image;
    }
  } else {
    if (isVerifierAnnotation) {

      const image = await new Promise<fabric.Object>((resolve) => {
        fabric.loadSVGFromString(annotationDefinitionCore.icon, function (objects, options) {
          const img = fabric.util.groupSVGElements(objects, options);
          img.name = annotationName;
          resolve(img);
        });
      });
      convertToViolet(image);
      return image;
    }

    const svgCacheKey = getAnnotationIconCacheKey(annotationDefinitionCore, annotation.annotationDefinitionVersionId);
    const svgFromCache = svgCache.get(svgCacheKey);

    if (!svgFromCache) {
      const loadedImage = await new Promise<fabric.Object>((resolve) => {
        fabric.loadSVGFromString(annotationDefinitionCore.icon, function (objects, options) {
          const image = fabric.util.groupSVGElements(objects, options);
          image.name = annotationName;
          resolve(image);
        });
      });

      svgCache.set(svgCacheKey, loadedImage);

      const firstClone = await new Promise<fabric.Object>((resolve) => {
        loadedImage.clone((obj: fabric.Object) => {
          obj.name = annotationName;
          resolve(obj);
        });
      });
      return firstClone;
    } else {
      const clone = await new Promise<fabric.Object>((resolve) => {
        svgFromCache.clone((obj: fabric.Object) => {
          resolve(obj);
        });
      });
      clone.name = annotationName;
      return clone;
    }
  }
  return null;
};

export const getActiveAndInactiveAnnotations = (
  annotations: AnnotationOnImage[],
  activeCriterion: ExamScoringSchema | null,
): AnnotationsLayers => {
  const inactiveAnnotations: AnnotationOnImage[] = [];
  const activeAnnotations: AnnotationOnImage[] = [];
  for (let i = 0; i < annotations.length; i++) {
    const annotation = annotations[i];
    if (isAnnotationActive(annotation, activeCriterion)) {
      activeAnnotations.push(annotation);
      continue;
    }
    inactiveAnnotations.push(annotation);
  }

  return {
    active: activeAnnotations.sort((a, b) => (a.layer ?? 0) - (b.layer ?? 0)),
    inactive: inactiveAnnotations,
  };
};

export const getNewHighestLayerNumber = (annotations: AnnotationImageData[]): number => {
  if (annotations.length === 0) return 0;

  return Math.max(...annotations.map((x) => x.annotation.layer ?? 0)) + 1;
};

export const removeAnnotationImagesFromCanvas = (
  activeAnnotationsRef: React.MutableRefObject<AnnotationImageData[]>,
  annotationImages: AnnotationImageData[],
  canvas: fabric.Canvas,
): void => {
  activeAnnotationsRef.current = [];
  annotationImages.forEach((obj: AnnotationImageData) => {
    canvas.remove(obj.image);
  });

  canvas.getObjects().forEach((item) => {
    if (item.name?.includes(ANNOTATION_OBJECT_NAME)) {
      canvas.remove(item);
    }
  });
};

export const rotateAnnotation = (
  annotationImage: AnnotationImageData,
  pageImageCoords: Coords,
  rotateAngle: rotateAngleValue,
  currentCoords: Coords | null | undefined,
): void => {
  setImageCoords(
    annotationImage.annotation,
    annotationImage.image,
    annotationImage.annotationDefinitionCore,
    rotateAngle,
    currentCoords,
  );
  if (annotationImage.annotation.positionX !== null && annotationImage.annotation.positionY !== null) {
    const { leftOffset, topOffset } = getImageRotateOffsetForIcons(
      rotateAngle,
      annotationImage.annotation.positionX,
      annotationImage.annotation.positionY,
    );
    annotationImage.image.set({
      left: pageImageCoords.left + leftOffset,
      top: pageImageCoords.top + topOffset,
    });

    if (annotationImage.annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
      annotationImage.image.angle =
        annotationImage.annotation.angle !== null ? annotationImage.annotation.angle - rotateAngle : rotateAngle;
    }
  }
};

export const getAnnotationWithUpdatedLayers = (
  annotationIdMoved: string,
  annotationsCopy: AnnotationOnImage[],
  direction: 'up' | 'down',
): AnnotationOnImage[] => {
  if (direction === 'down') {
    let idx = 1;
    for (let i = 0; i < annotationsCopy.length; i++) {
      const annotation = annotationsCopy[i];
      if (annotation.annotationId === annotationIdMoved) {
        annotation.layer = 0;
      } else {
        annotation.layer = idx;
        idx++;
      }
    }
    return annotationsCopy;
  }

  let idx = annotationsCopy.length - 2;
  for (let i = 0; i < annotationsCopy.length; i++) {
    const annotation = annotationsCopy[i];
    if (annotation.annotationId === annotationIdMoved) {
      annotation.layer = annotationsCopy.length - 1;
    } else {
      annotation.layer = idx;
      idx--;
    }
  }

  return annotationsCopy;
};
